import type {
  Dispatch,
  PropsWithChildren,
  RefObject,
  SetStateAction
} from 'react'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import type { AudioPlayerControls } from 'react-use-audio-player'
import {
  AudioPlayerProvider,
  useAudioPlayer,
  useAudioPosition
} from 'react-use-audio-player'
import type { HowlOptions } from 'howler'
import { Howler } from 'howler'
import { useMoteListenActivityMutation } from '@mote/api'
import type { ElementSize, PlaybackRate } from '@mote/helpers'
import {
  PLAYBACK_RATES,
  isNumeric,
  useElementSize,
  useEventListener
} from '@mote/helpers'
import { isMac, isWindows, preventBubblingEvent } from '@mote/helpers/browser'
import { TRACK_EVENT_NAMES, noop } from '@mote/helpers/constants'
import type { AudioData, AudioDataMinimal } from '@mote/helpers/media'
import { getAudioData } from '@mote/helpers/media'
import type { UseMoteTrackMutation } from '../track'
import { convertToListeningContext, useMoteTrackMutate } from '../track'

const SEEK_SLOW_SECONDS = 2
const SEEK_FAST_SECONDS = 5
const DEFAULT_PLAYBACK_CALLBACKS = {
  onPlay: noop,
  onPause: noop,
  onEnd: noop,
  onReplay: noop,
  onDelete: noop,
  onFullTranscript: noop
}

export const dummyAudioDataMinimal: AudioDataMinimal = {
  durationInSeconds: 1,
  channelWaveforms: [
    new Float32Array([
      0, 0, 0.3, 0.16, 0, 0.68, 0.84, 0.82, 0.38, 0.2, 0.05, 0.47, 0.73, 1,
      0.56, 0.12, 0.16, 0.29, 0.28, 0.19, 0.06, 0.56, 0.72, 0, 0, 0.24, 0.3,
      0.02, 0, 0.14, 0.9, 0.34
    ])
  ]
}

type PlaybackCallbacks = typeof DEFAULT_PLAYBACK_CALLBACKS

type PlayerProps = {
  ref: RefObject<HTMLDivElement>
  tabIndex: number
  className: string
  role: string
  'aria-label': string
  onMouseDown?: () => void
  onKeyDown?: (event: React.KeyboardEvent) => void
  onKeyUp?: (event: React.KeyboardEvent) => void
}

export interface MotePlayerContextValues
  extends AudioPlayerControls,
    ReturnType<typeof useAudioPosition> {
  state: 'playing' | 'paused' | 'idle'
  play: () => void
  pause: () => void
  paused: boolean
  onScrubberValueChange: (value: number) => void
  onScrubberValueChangeEnd: (value: number) => void
  onVolumeChange: (value: number) => void
  onRewind: () => void
  onForward: () => void
  onDelete: () => void
  onMute: Dispatch<SetStateAction<boolean>>
  toggleMute: () => void
  getAudioPosition: () => number
  muted: boolean
  internalVolume: number
  preMuteValue: number
  increaseVolume: () => void
  decreaseVolume: () => void
  getPlayerProps: () => PlayerProps
  addPlaybackCallbacks: (callbacks: PlaybackCallbacks) => void
  isReady: boolean
  handleOnAudioLoad: () => void
  handleTracking: UseMoteTrackMutation
  moteId: string
  replay: () => void
  onPlaybackRateChange: (
    rate: PlaybackRate,
    index: number
  ) => boolean | undefined
  playbackRate: PlaybackRate
  playerDimensions: ElementSize
  active: boolean
  setActive: Dispatch<SetStateAction<boolean>>
  toggleFullTranscript?: () => void
  showFullTranscript?: boolean
  theme?: Record<string, any>
  transcriptRef?: RefObject<HTMLElement>
  audioSource: string
  audioData: AudioData | undefined
  getMinimalAudioData: () => AudioDataMinimal
}

export const MotePlayerContext = createContext<MotePlayerContextValues | null>(
  null
)

export interface MotePlayerProviderProps {
  moteId: string
  moteCreatedByUserId: string
  audioSource: string
  isStandalone?: boolean
  isLandingPage?: boolean
  isRichCard?: boolean
  isEmbed?: boolean
  theme?: Record<string, any>
}

const MotePlayerProviderRoot = ({
  isRichCard = false,
  isLandingPage = false,
  isEmbed = false,
  audioSource,
  theme = {},
  moteId,
  moteCreatedByUserId,
  children
}: PropsWithChildren<MotePlayerProviderProps>) => {
  const moteTrackMutate = useMoteTrackMutate(
    convertToListeningContext(isLandingPage, isEmbed),
    moteId,
    moteCreatedByUserId
  )
  const moteListenActivityMutation = useMoteListenActivityMutation({
    moteId
  })
  const {
    play,
    pause,
    player,
    playing,
    volume,
    loading,
    ready,
    ended,
    ...restAudioPlayer
  } = useAudioPlayer()
  const { position, duration, seek, ...restAudioPosition } = useAudioPosition()
  const [playbackCallbacks, setPlaybackCallbacks] = useState(
    DEFAULT_PLAYBACK_CALLBACKS
  )
  const [audioData, setAudioData] = useState<AudioData>()
  const [active, setActive] = useState<boolean>(false)
  const [paused, setPaused] = useState<boolean>(!playing)
  const [muted, setMuted] = useState<boolean>(false)
  const [localVolume, setLocalVolume] = useState<number>(100)
  const [preMuteValue, setPreMuteValue] = useState<number>(100)
  const [showFullTranscript, setShowFullTranscript] = useState<boolean>(false)
  const [playbackRate, setPlaybackRate] = useState<PlaybackRate>(
    PLAYBACK_RATES[1]
  )
  const playedOnceRef = useRef<boolean>(false)
  const prevPlayingState = useRef<boolean | null>(null)
  const transcriptRef = useRef<HTMLElement>(null)
  const currentRateIndexRef = useRef<number>(1)
  const transcriptCurrentScrollRef = useRef<number>(0)
  const { ref: playerRef, ...playerDimensions } =
    useElementSize<HTMLDivElement>()
  const isReady = !loading && ready

  const handleOnAudioLoad = async () => {
    if (!audioSource || audioData) return

    const soundExists = Howler._howls.find(howl => howl._src === audioSource)

    if (!soundExists) return

    setAudioData(await getAudioData(audioSource, Howler.audioBuffer))
  }

  const handleOnEnd = useCallback(() => {
    playbackCallbacks.onEnd?.()
    moteTrackMutate(TRACK_EVENT_NAMES.mote.playback.end)
  }, [playbackCallbacks])

  useEffect(() => {
    if (!player) return
    player.on('end', handleOnEnd)

    return () => {
      player.off('end', handleOnEnd)
    }
  }, [player, handleOnEnd])

  const addPlaybackCallbacks = (functions: Partial<PlaybackCallbacks> = {}) => {
    if (Object.keys(functions).length === 0) return

    setPlaybackCallbacks(prev => ({ ...prev, ...functions }))
  }

  const getAudioPosition = () => (position ? Math.round(position * 10) / 10 : 0)

  const getMinimalAudioData = (): AudioDataMinimal => {
    if (!audioData) return dummyAudioDataMinimal

    return {
      durationInSeconds: audioData.durationInSeconds,
      channelWaveforms: audioData.channelWaveforms
    }
  }

  const toggleFullTranscript = () => {
    setShowFullTranscript(prev => {
      const state = !prev

      moteTrackMutate(
        `${TRACK_EVENT_NAMES.mote.action.fullTranscript} (${
          state ? 'Show' : 'Hide'
        })`
      )

      return state
    })
    transcriptCurrentScrollRef.current = 0
    playbackCallbacks.onFullTranscript?.()
  }

  const handlePlaybackRateChange = (newRate: PlaybackRate, index: number) => {
    if (!player) return
    if (!isNumeric(newRate)) return false

    const rate = +newRate as PlaybackRate

    player.rate(rate)
    setPlaybackRate(rate)
    currentRateIndexRef.current = index

    moteTrackMutate(TRACK_EVENT_NAMES.mote.playback.speed, {
      speed: rate
    })

    return true
  }

  const handlePlay = () => {
    play()
    setPaused(false)
    playbackCallbacks.onPlay?.()

    moteTrackMutate(TRACK_EVENT_NAMES.mote.playback.play)

    if (!playedOnceRef.current) {
      try {
        moteListenActivityMutation.mutate()
      } catch {}
      playedOnceRef.current = true
    }
  }

  const handlePause = () => {
    pause()
    setPaused(true)
    playbackCallbacks.onPause?.()

    moteTrackMutate(TRACK_EVENT_NAMES.mote.playback.pause)
  }

  const handleReplay = () => {
    handlePlay()
    playbackCallbacks.onReplay?.()

    moteTrackMutate(TRACK_EVENT_NAMES.mote.playback.replay)
  }

  const handleOnVolumeChange = (value: number) => {
    if (player) player.mute(!value)

    setMuted(!value)
    setLocalVolume(value)
    volume(value / 100)
  }

  const increaseVolume = () => {
    if (localVolume >= 100) return

    handleOnVolumeChange(localVolume + 10)
  }

  const decreaseVolume = () => {
    if (localVolume <= 0) return

    handleOnVolumeChange(localVolume - 10)
  }

  const toggleMute = () => {
    if (!player) return
    if (!muted && localVolume) setPreMuteValue(localVolume)
    setMuted(!muted)
    handleOnVolumeChange(!muted ? 0 : preMuteValue)
    player.mute(!muted)
  }

  const handleOnDelete = () => {
    playbackCallbacks.onDelete?.()

    moteTrackMutate(TRACK_EVENT_NAMES.mote.action.delete)
  }

  const handleOnRewind = (speed = SEEK_FAST_SECONDS) => {
    seek(Math.max(0, position - speed))

    moteTrackMutate(TRACK_EVENT_NAMES.mote.playback.rewind)
  }

  const handleOnForward = (speed = SEEK_FAST_SECONDS) => {
    seek(Math.min(duration, position + speed))

    moteTrackMutate(TRACK_EVENT_NAMES.mote.playback.forward)
  }

  const handleScrubberValueChange = (value: number) => {
    prevPlayingState.current = playing
    if (playing) {
      handlePause()
    }
    seek(value)
  }

  const handleScrubberValueChangeEnd = () => {
    if (prevPlayingState.current) {
      handlePlay()
    }
  }

  const handleOnPlayerKeydown = (event: React.KeyboardEvent) => {
    if (
      (!isLandingPage && document.activeElement !== playerRef.current) ||
      (event.target as HTMLInputElement).tagName === 'INPUT'
    ) {
      return
    }

    if (isRichCard) {
      if (!active && !ended) {
        switch (event.key) {
          case ' ':
            preventBubblingEvent(event)
            handlePlay()
            break
        }
        return
      }
    }

    if (!active && ended) {
      switch (event.key) {
        case 'r':
          preventBubblingEvent(event)
          handleReplay()
          break
      }

      return
    }

    const isCtrlKey =
      (isWindows() && event.ctrlKey) || (isMac() && event.metaKey)

    switch (event.key) {
      case ' ':
      case 'k':
        preventBubblingEvent(event)
        if (playing) {
          handlePause()
          break
        }
        handlePlay()
        break
      case 'm':
        preventBubblingEvent(event)
        toggleMute()
        break
      case 'ArrowUp':
        preventBubblingEvent(event)
        increaseVolume()
        break
      case 'ArrowDown':
        preventBubblingEvent(event)
        decreaseVolume()
        break
      case 'j':
        preventBubblingEvent(event)
        handleOnRewind()
        break
      case 'ArrowLeft':
        preventBubblingEvent(event)
        handleOnRewind(SEEK_SLOW_SECONDS)
        break
      case 'l':
        preventBubblingEvent(event)
        handleOnForward()
        break
      case 'ArrowRight':
        preventBubblingEvent(event)
        handleOnForward(SEEK_SLOW_SECONDS)
        break
      case 'd':
        preventBubblingEvent(event)
        handleOnDelete()
        break
      case 'c':
        if (!isRichCard) return
        if (isCtrlKey) return

        preventBubblingEvent(event)
        toggleFullTranscript()
        break
      case 'z':
        if (!isRichCard) return
        if (!showFullTranscript) return
        if (isCtrlKey) return
        preventBubblingEvent(event)
        transcriptCurrentScrollRef.current -= 25

        if (transcriptCurrentScrollRef.current < 0)
          transcriptCurrentScrollRef.current = 0

        transcriptRef.current?.scroll({
          top: transcriptCurrentScrollRef.current,
          behavior: 'smooth'
        })
        break
      case 'x': {
        if (!isRichCard) return
        if (!transcriptRef.current) return
        if (!showFullTranscript) return
        if (isCtrlKey) return
        preventBubblingEvent(event)

        const maxScroll = transcriptRef.current.scrollHeight
        const scrollWithOffset =
          transcriptCurrentScrollRef.current +
          25 +
          transcriptRef.current.offsetHeight

        const shouldAddScroll = scrollWithOffset <= maxScroll

        if (shouldAddScroll) transcriptCurrentScrollRef.current += 25

        transcriptRef.current?.scroll({
          top: shouldAddScroll ? transcriptCurrentScrollRef.current : maxScroll,
          behavior: 'smooth'
        })

        break
      }

      case ',': {
        preventBubblingEvent(event)

        if (currentRateIndexRef.current === 0) return
        let prevRateIndex = currentRateIndexRef.current - 1

        if (prevRateIndex < 0) prevRateIndex = 0

        handlePlaybackRateChange(PLAYBACK_RATES[prevRateIndex], prevRateIndex)
        break
      }
      case '.': {
        preventBubblingEvent(event)

        if (currentRateIndexRef.current === PLAYBACK_RATES.length) return
        let nextRateIndex = currentRateIndexRef.current + 1

        if (nextRateIndex > PLAYBACK_RATES.length)
          nextRateIndex = PLAYBACK_RATES.length

        handlePlaybackRateChange(PLAYBACK_RATES[nextRateIndex], nextRateIndex)
        break
      }
    }
  }

  const handleOnPlayerKeyUp = (event: React.KeyboardEvent) => {
    if (!isLandingPage && document.activeElement !== playerRef.current) return

    if (isRichCard) {
      if (!active && !ended) {
        switch (event.key) {
          case ' ':
            event.preventDefault()
            break
        }
        return
      }
    }

    if (!active && ended) {
      switch (event.key) {
        case 'r':
          event.preventDefault()
          break
      }

      return
    }

    const isCtrlKey =
      (isWindows() && event.ctrlKey) || (isMac() && event.metaKey)

    switch (event.key) {
      case ' ':
      case 'm':
      case 'j':
      case 'k':
      case 'l':
      case 'd':
      case ',':
      case '.':
        event.preventDefault()
        break
      case 'z':
      case 'x':
        if (!isRichCard) return
        if (!showFullTranscript) return
        if (isCtrlKey) return

        event.preventDefault()
        break
      case 'c':
        if (isCtrlKey) return

        event.preventDefault()
        break
    }
  }

  const handleOnPlayerClick = () => {
    playerRef.current?.focus()
  }

  const getPlayerProps = () => {
    let props: PlayerProps = {
      ref: playerRef,
      tabIndex: 0,
      className: 'mote-card',
      role: 'region',
      'aria-label': 'Mote Player'
    }

    if (!isLandingPage && isReady) {
      props = {
        ...props,
        onMouseDown: handleOnPlayerClick,
        onKeyDown: handleOnPlayerKeydown,
        onKeyUp: handleOnPlayerKeyUp
      }
    }

    return props
  }

  useEventListener('keydown', handleOnPlayerKeydown, null, {
    enabled: isReady && isLandingPage
  })
  useEventListener('keyup', handleOnPlayerKeyUp, null, {
    enabled: isReady && isLandingPage
  })

  return (
    <MotePlayerContext.Provider
      value={{
        play: handlePlay,
        pause: handlePause,
        player,
        playing,
        paused,
        volume,
        loading,
        ready,
        ended,
        ...restAudioPlayer,
        position,
        duration,
        seek,
        ...restAudioPosition,
        onScrubberValueChange: handleScrubberValueChange,
        onScrubberValueChangeEnd: handleScrubberValueChangeEnd,
        onVolumeChange: handleOnVolumeChange,
        onRewind: handleOnRewind,
        onForward: handleOnForward,
        onDelete: handleOnDelete,
        onMute: setMuted,
        toggleMute,
        getAudioPosition,
        muted,
        internalVolume: localVolume,
        preMuteValue,
        increaseVolume,
        decreaseVolume,
        getPlayerProps,
        addPlaybackCallbacks,
        isReady,
        handleOnAudioLoad,
        handleTracking: moteTrackMutate,
        replay: handleReplay,
        playbackRate,
        onPlaybackRateChange: handlePlaybackRateChange,
        playerDimensions,
        moteId,
        active,
        setActive,
        audioSource,
        audioData,
        getMinimalAudioData,
        state:
          ended || restAudioPlayer.stopped
            ? 'idle'
            : playing
            ? 'playing'
            : 'paused',
        ...(isRichCard
          ? {
              toggleFullTranscript,
              showFullTranscript,
              theme,
              transcriptRef
            }
          : {})
      }}
    >
      {children}
    </MotePlayerContext.Provider>
  )
}

export const MotePlayerProvider = (
  props: PropsWithChildren<MotePlayerProviderProps>
): JSX.Element => {
  const {
    children,
    isStandalone = true,
    isLandingPage = false,
    ...rest
  } = props

  return isStandalone ? (
    <AudioPlayerProvider>
      <MotePlayerProviderRoot isLandingPage={isLandingPage} {...rest}>
        {children}
      </MotePlayerProviderRoot>
    </AudioPlayerProvider>
  ) : (
    <>{children}</>
  )
}

export interface UseMotePlayerOptions extends Omit<HowlOptions, 'src'> {
  init?: boolean
  src?: HowlOptions['src']
  highRefreshRate?: boolean
  html5?: boolean
  autoplay?: boolean
  initialPercentagePosition?: number
  playbackCallbacks?: PlaybackCallbacks
  isStandalone?: boolean
  isRichCard?: boolean
}

export const useMotePlayer = (options: UseMotePlayerOptions = {}) => {
  const context = useContext(MotePlayerContext)

  const {
    init = false,
    highRefreshRate = true,
    src = '',
    html5 = true,
    preload = true,
    format = ['mp3'],
    autoplay = false,
    initialPercentagePosition,
    playbackCallbacks,
    isStandalone = true,
    isRichCard = false
  } = useMemo(() => options, [options])

  const audioPlayerConfig = useMemo(
    () =>
      init && context
        ? {
            src,
            preload,
            html5,
            format,
            autoplay,
            onload: context.handleOnAudioLoad
          }
        : {},
    [init, src, preload, html5, isRichCard, format, autoplay]
  ) as HowlOptions

  const audioPositionConfig = useMemo(
    () =>
      init
        ? {
            highRefreshRate
          }
        : {},
    [init, highRefreshRate]
  )

  useAudioPlayer(audioPlayerConfig)
  const { seek, duration } = useAudioPosition(audioPositionConfig)

  if (init && initialPercentagePosition) {
    seek((initialPercentagePosition * duration) / 100)
  }

  useEffect(() => {
    if (!isStandalone || !init || !playbackCallbacks || !context) return
    if (!init) return

    context.addPlaybackCallbacks(playbackCallbacks)
  }, [isStandalone, init, playbackCallbacks])

  if (context === null) {
    throw new Error(
      'Attempted to call useMotePlayer outside the MoteCard component.'
    )
  }

  return context
}
